import * as v0 from "./survey";
import { traverseForKind } from "./traverse";
import { pruneForUserTags } from "./translation/permissions";

/** First comms channel matching channelName, or undefined. */
export function getChannelConfigFromConfig(
  config: v0.CommsConfig, channelName: string
): v0.CommsChannel | undefined {
  return [config.default, ...(config.otherChannels || [])].find(c => c && c.channelName === channelName);
}

/**
 * Resolves the payments each Portal Payments component on the page should
 * show. Tag limits (limitedToUsersTagged / a collection's limitedToTags) are
 * a STAFF access-control mechanism, not a way to hide an applicant's own
 * payments from them:
 * - Applicants see every payment they've received — no tag pruning (omit perms).
 * - Staff (the /ao/ view) see only payments their role can access — pass their
 *   perms to prune the survey first.
 * This matches the per-token pruning addPortalPaymentFields applies when
 * building candidate info, so the served list and the available fields agree.
 */
export function resolveServedPortalPayments(
  portalPage: v0.ApplicantPortalPage,
  surveyRoot: v0.ExpandedSurvey['survey'],
  perms?: string[],
): void {
  const paymentsComponents = traverseForKind<v0.PortalPayments>(portalPage, 'Portal Payments');
  if (!paymentsComponents.length) return;

  const visibleSurvey = perms ? pruneForUserTags(surveyRoot, perms) : surveyRoot;
  const visiblePayments = traverseForKind<v0.Payment>(visibleSurvey, 'Payment');
  for (const component of paymentsComponents) {
    const include = component.paymentsToInclude?.length ? new Set<string>(component.paymentsToInclude) : undefined;
    (component as v0.ServedPortalPayments).payments = visiblePayments
      .filter(p => !include || include.has(p.targetField))
      .map(p => ({ targetField: p.targetField, name: p.name, type: p.type }));
  }
}

/**
 * Resolves the server-derived fields a portal page's components need before
 * being served to a client: support-ticket issue-type options + phone (from
 * comms config) and the payments list (from the survey). Mutates portalPage in
 * place. Pure over its inputs so it can run on the backend (getPortal) or
 * client-side against in-progress distro-editor config for preview.
 *
 * Pass `perms` only for a staff viewer; omit for the applicant view (no
 * payment pruning).
 */
export function resolvePortalPageForServing(
  portalPage: v0.ApplicantPortalPage,
  opts: {
    comms?: v0.CommsConfig,
    surveyRoot: v0.ExpandedSurvey['survey'],
    perms?: string[],
  },
): void {
  for (const form of traverseForKind<v0.PortalSupportTicketForm>(portalPage, 'Portal Support Ticket Form')) {
    const channel = opts.comms && getChannelConfigFromConfig(opts.comms, form.channel);
    const served = form as v0.ServedPortalSupportTicketForm;
    if (form.issueTypes) {
      served.issueTypes = {
        options: (channel && channel.supportDashboard?.issueTypes?.options) || [],
      };
    }
    served.contact = {
      phone: (channel && channel.twilioConfig?.number) || undefined,
    };
  }

  resolveServedPortalPayments(portalPage, opts.surveyRoot, opts.perms);
}
